import { Chip, IconButton, Table, TableBody, TableCell, TableContainer, TablePagination, TableRow } from "@mui/material";
import { Visibility } from "@mui/icons-material";
import { useState } from "react";
import dayjs from "dayjs";

import TableWrapperStyle from "@/components/app/ContentTable/style";
import { Order } from "@/components/app/ContentTable/types";
import EnhancedTableHead from "@/components/app/EnhancedTableHeader";
import TableSkeleton from "@/components/app/TableSkeleton";
import InitLoading from "@/components/app/Loading/pageLoading";
import { IAdminLogItem } from "@/api/types/admin-logs";
import headCells, { Data } from "./tableHeadCells";
import LogDetailModal from "./LogDetailModal";

export function createData(data: IAdminLogItem): Data {
    const {
        id,
        user,
        action,
        entity,
        entityId,
        method,
        statusCode,
        ipAddress,
        isError,
        createdAt,
        requestBody,
        responseBody,
    } = data;

    return {
        id,
        user: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email || "Unknown",
        action,
        entity,
        entityId,
        method,
        statusCode,
        ipAddress,
        isError,
        createdAt: dayjs(createdAt).format("YYYY-MM-DD HH:mm:ss"),
        requestBody,
        responseBody,
    };
}

export default function AdminLogsTable({
    data: rows,
    totalItems,
    rowsPerPage,
    page,
    onChange,
    isLoading,
    isFetching,
    isPending,
}: {
    data: Data[],
    isPending: boolean,
    isLoading: boolean,
    isFetching: boolean,
    totalItems: number,
    page: number,
    rowsPerPage: number,
    onChange: (data: { page?: number, rowsPerPage?: number }) => void,
}) {
    const [order, setOrder] = useState<Order>("desc");
    const [orderBy, setOrderBy] = useState<keyof Data>("createdAt");
    const [selectedLog, setSelectedLog] = useState<Data | null>(null);
    const [modalOpen, setModalOpen] = useState(false);

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (event: unknown, newPage: number) => onChange({ page: newPage, rowsPerPage });
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) =>
        onChange({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });

    const handleViewDetails = (log: Data) => {
        setSelectedLog(log);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedLog(null);
    };

    const getStatusColor = (statusCode: number) => {
        if (statusCode >= 200 && statusCode < 300) return "success";
        if (statusCode >= 400 && statusCode < 500) return "warning";
        if (statusCode >= 500) return "error";
        return "default";
    };

    const getActionColor = (action: string) => {
        switch (action.toUpperCase()) {
            case "CREATE":
                return "success";
            case "UPDATE":
                return "info";
            case "DELETE":
                return "error";
            case "VIEW":
                return "primary";
            case "LOGIN":
                return "success";
            case "LOGOUT":
                return "warning";
            case "EXPORT":
                return "primary";
            case "IMPORT":
                return "secondary";
            case "ACTIVATE":
                return "success";
            case "DEACTIVATE":
                return "error";
            case "APPROVE":
                return "success";
            case "REJECT":
                return "error";
            case "OTHER":
                return "default";
            default:
                return "default";
        }
    };

    return (
        <TableWrapperStyle className="table-style-wrapper" sx={{ width: "100%", marginTop: "2em" }}>
            <TableContainer>
                <Table
                    stickyHeader
                    aria-labelledby="tableTitle"
                    className="table"
                    component="div"
                    size="medium"
                    sx={{ padding: ".5em" }}
                >
                    <EnhancedTableHead<Data>
                        headCells={headCells}
                        order={order}
                        orderBy={orderBy}
                        rowCount={rows.length}
                        onRequestSort={handleRequestSort}
                    />
                    <TableBody component="div">
                        {isLoading && <TableSkeleton cells={headCells.map((item) => item.id)} />}
                        {!isLoading && !isFetching && rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={headCells.length}>No Data To Show</TableCell>
                            </TableRow>
                        )}
                        {isFetching && <InitLoading />}
                        {rows.map((row) => {
                            const { id, user, action, entity, entityId, method, statusCode, ipAddress, isError, createdAt } =
                                row;

                            return (
                                <TableRow key={id} className="table-row" component="div" role="checkbox" tabIndex={-1}>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{id}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{user}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">
                                            <Chip
                                                color={getActionColor(action) as any}
                                                label={action}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{entity}</span>
                                    </TableCell>
                                    {/* <TableCell align="center" className="table-cell" component="div">
                    <span className="table-value">{entityId}</span>
                  </TableCell> */}
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">
                                            <Chip
                                                color={
                                                    method === "GET"
                                                        ? "info"
                                                        : method === "POST"
                                                          ? "success"
                                                          : method === "PUT"
                                                            ? "warning"
                                                            : "error"
                                                }
                                                label={method}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">
                                            <Chip
                                                color={getStatusColor(statusCode) as any}
                                                label={statusCode}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{ipAddress}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">
                                            <Chip
                                                color={isError ? "error" : "success"}
                                                label={isError ? "Error" : "Success"}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{createdAt}</span>
                                    </TableCell>
                                    <TableCell align="right" className="table-cell" component="div">
                                        <span className="table-value" style={{ padding: "5px", justifyContent: "flex-end" }}>
                                            <IconButton onClick={() => handleViewDetails(row)}>
                                                <Visibility />
                                            </IconButton>
                                        </span>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={totalItems}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
            <LogDetailModal open={modalOpen} onClose={handleCloseModal} log={selectedLog} />
        </TableWrapperStyle>
    );
}
